import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

type Kind = "done" | "now" | "open" | "locked";

/**
 * Лента трёх шагов рисуется полосой вкладок карточки — другой навигации в
 * модуле нет. Но выглядеть она должна лентой: одна карточка во всю ширину,
 * три равные доли, разделитель между ними.
 *
 * Системный слой при этом не трогаем: полосу красит правило ниже, и
 * привязано оно к нашему же шагу (`[data-return-step]`). Чужая полоса
 * вкладок под него не попадает, а если браузер не знает `:has()`, лента
 * просто останется обычными вкладками — но не сломается.
 */
export const ribbon = (theme: ExactTheme) =>
  css({
    ".SimpleTabs--headers:has([data-return-step])": {
      // Высота у полосы вкладок своя, 35px: без этого шаг остаётся плоской
      // вкладкой, сколько ему ни задай отбивки.
      height: "auto",
      // Ширина своя, в 100%: с ней отбивки по бокам не сужали полосу, а
      // выталкивали её за правый край — левый совпадал с панелями, правый
      // уходил на 30 точек. `auto` отдаёт ширину растягиванию, и поля
      // вычитаются из неё.
      width: "auto",
      margin: "0 15px",
      padding: 0,
      background: theme.colorsV2.white,
      border: `1px solid ${theme.colorsV2.grey[100]}`,
      // Радиус как у панелей под лентой: 8 против 6 читался как второй приём
      // в одном вертикальном стеке.
      borderRadius: 6,
      overflow: "hidden",

      // Группа ярлыков растягивается, а каждый шаг берёт свою треть.
      // Правила адресованы только ей: следом за группой стоит кнопка
      // переполнения, и шириной шага она не является.
      // `maxWidth` системный слой держит под кнопку переполнения; её мы
      // прячем, и без снятия ограничения лента не доходила до правого края
      // карточки на 31 точку — подсветка активного шага обрывалась, за ней
      // оставался белый язык.
      "& > div:first-of-type": { flex: 1, minWidth: 0, maxWidth: "100%" },

      "& > div:first-of-type > button": {
        flex: 1,
        minWidth: 0,
        height: "auto",
        padding: "11px 16px",
        borderRadius: 0,
        justifyContent: "flex-start",
        borderLeft: `1px solid ${theme.colorsV2.grey[100]}`,
      },

      "& > div:first-of-type > button:first-of-type": { borderLeft: "none" },

      /*
        Фокус с клавиатуры виден.

        Полоса вкладок гасит обводку и отмечает фокус фоном, а наше правило
        «вы здесь» красит фон сильнее и по специфичности выше — фокус
        пропадал совсем, и по единственной навигации экрана приходилось идти
        вслепую.
      */
      "& > div:first-of-type > button:focus-visible": {
        outline: `2px solid ${theme.colorsV2.blue[600]}`,
        outlineOffset: -2,
      },

      // Кнопка переполнения полосы вкладок: трёх шагов она не касается, а
      // пустой белый язык справа внутри рамки оставляла.
      "& > div:not(:first-of-type)": { display: "none" },

      "& .tab-title": { width: "100%", minWidth: 0 },

      /*
        Наведение красит шаг целиком, а не кусок с текстом.

        Полоса вкладок подсвечивает внутренний `.tab-title`, и в узком ярлыке
        это незаметно. У шага на треть ширины подсветка получалась плашкой
        посередине: мышь на шаге, а светится его середина.
      */
      "& > div:first-of-type > button:not(:disabled):hover": {
        background: theme.colorsV2.grey[50],
      },

      "& > div:first-of-type > button:hover .tab-title": {
        background: "transparent",
      },

      // Подсвеченный шаг остаётся собой и под мышью: синий фон сильнее
      // серого, иначе наведение «выключает» отметку «вы здесь».
      "& > div:first-of-type > button:has([data-return-step-here]):hover": {
        background: theme.colorsV2.blue[25],
      },

      /*
        На узком экране ширину забирает текущий шаг.

        По трети на каждый — это правило широкого экрана: в 390 px треть не
        вмещает ни одного названия, и все три обрезаются в «Rü… W… Re…».
        Соседние шаги сжимаются до кружка (текст у них прячется в `step`), а
        освободившееся достаётся тому, на котором стоят.
      */
      "@media (max-width: 680px)": {
        "& > div:first-of-type > button": {
          flex: "0 0 auto",
          padding: "11px 12px",
        },
        "& > div:first-of-type > button:has([data-return-step-here])": {
          flex: 1,
        },
      },

      // Свой разделитель системы здесь лишний: шаги делит рамка.
      "& .tab-separator": { display: "none" },

      // Подсвечен тот шаг, на котором стоим, — это ответ на вопрос «где я»,
      // а не «что сделано»: сделанное говорит галочка в кружке.
      "& button:has([data-return-step-here])": {
        background: theme.colorsV2.blue[25],
        boxShadow: `inset 0 -2px 0 ${theme.colorsV2.blue[500]}`,
      },

      "& button:has([data-return-step='locked'])": {
        background: theme.colorsV2.grey[25],
      },
    },
  });

/** Содержимое шага: номер, название, подпись с состоянием. */
export const step = (kind: Kind) =>
  css({
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minWidth: 0,
    textAlign: "left",
    // Недоступный шаг виден и приглушён: исчезать он не имеет права, иначе
    // теряется сама идея «три шага».
    opacity: kind === "locked" ? 0.55 : 1,

    /*
      На узком экране лента оставляет текст только текущему шагу.

      Три названия в 390 px не помещаются и обрезаются до «Rü… W… Re…» —
      это хуже, чем их отсутствие: номера и так видны кружками, а
      обрезанное слово читается как поломка. Соседние шаги остаются
      кружками, лента из трёх шагов никуда не девается.
    */
    "@media (max-width: 680px)": {
      "& [data-step-text]": { display: "none" },
      "&[data-return-step-here] [data-step-text]": { display: "flex" },
      width: "auto",
    },
  });

export const mark = (kind: Kind) => (theme: ExactTheme) =>
  css({
    width: 22,
    height: 22,
    borderRadius: "50%",
    flex: "none",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 600,
    ...{
      done: {
        background: theme.colorsV2.green[50],
        color: theme.colorsV2.green[700],
      },
      now: {
        background: theme.colorsV2.blue[600],
        color: theme.colorsV2.white,
      },
      open: {
        background: theme.colorsV2.grey[100],
        color: theme.colorsV2.grey[700],
      },
      locked: {
        background: theme.colorsV2.grey[100],
        color: theme.colorsV2.grey[700],
      },
    }[kind],
  });

export const text = css({
  display: "flex",
  flexDirection: "column",
  gap: 1,
  // 1.5, как в макете: при 1.2 лента была на шесть точек ниже проектной и
  // строки стояли впритык.
  lineHeight: 1.5,
  minWidth: 0,
});

export const name = (theme: ExactTheme) =>
  css({
    fontSize: 14,
    fontWeight: 500,
    color: theme.colorsV2.grey[800],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

export const sub = (theme: ExactTheme) =>
  css({
    fontSize: 11.5,
    fontWeight: 400,
    color: theme.colorsV2.grey[700],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

export const lock = (theme: ExactTheme) =>
  css({
    fontSize: 11,
    color: theme.colorsV2.grey[700],
    marginLeft: 4,
  });
